import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PauseCircleRoundedIcon from "@mui/icons-material/PauseCircleRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import AddBusinessRoundedIcon from "@mui/icons-material/AddBusinessRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import FormatPaintRoundedIcon from "@mui/icons-material/FormatPaintRounded";
import DomainRoundedIcon from "@mui/icons-material/DomainRounded";
import LanguageRounded from "@mui/icons-material/LanguageRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import FlexibleStatsRow from "../../../components/common/FlexibleStatsRow";
import { whiteLabelApi } from "../../../redux/admin/whiteLabelApi";
import { toast } from "react-hot-toast";

const STAT_QUERY_PARAMS = Object.freeze({
  page: 1,
  limit: 100,
  sortBy: "createdAt",
  sortOrder: "desc",
});

function WhiteLabelMiniPanel({ title, subtitle, actions = null, children, minHeight = 0 }) {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.92),
        boxShadow:
          theme.palette.mode === "dark"
            ? `0 22px 48px ${alpha("#020617", 0.32)}`
            : `0 18px 40px ${alpha("#0f172a", 0.08)}`,
        minHeight,
      }}
    >
      <Stack spacing={2.5} sx={{ p: { xs: 2.25, md: 3 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack spacing={0.75}>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {subtitle}
              </Typography>
            ) : null}
          </Stack>
          {actions}
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
}

function getErrorMessage(error, fallbackMessage) {
  return (
    error?.data?.msg ||
    error?.data?.message ||
    error?.message ||
    fallbackMessage
  );
}

function formatRelativeDate(value) {
  if (!value) return "No recent activity";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No recent activity";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

async function mapWithConcurrency(items, concurrency, mapper) {
  const results = new Array(items.length);
  let index = 0;

  const workerCount = Math.max(1, Math.min(concurrency, items.length || 1));

  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (index < items.length) {
        const currentIndex = index;
        index += 1;
        results[currentIndex] = await mapper(items[currentIndex], currentIndex);
      }
    })
  );

  return results;
}

function DashboardPanelSkeleton({ rows = 4 }) {
  return (
    <Stack spacing={1.5}>
      {Array.from({ length: rows }).map((_, index) => (
        <Paper
          key={index}
          elevation={0}
          sx={{ borderRadius: 2.5, p: 2, border: "1px solid rgba(148, 163, 184, 0.18)" }}
        >
          <Skeleton width="35%" height={20} />
          <Skeleton width="70%" height={28} />
          <Skeleton width="55%" height={18} />
        </Paper>
      ))}
    </Stack>
  );
}

export default function WhiteLabelDashboardPage({ onNavigateSection }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    inactiveCompanies: 0,
    totalDomains: 0,
    totalWebsites: 0,
    recentCompanies: [],
    lastUpdatedAt: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isMounted = true;

    const executeQuery = async (endpoint, arg) => {
      const request = dispatch(
        endpoint.initiate(arg, {
          forceRefetch: true,
        })
      );

      try {
        return await request.unwrap();
      } finally {
        request.unsubscribe();
      }
    };

    const loadDashboard = async () => {
      setIsLoading(true);
      setErrorMessage("");

      try {
        const [allCompaniesFirstPage, activeCompaniesResult, inactiveCompaniesResult] =
          await Promise.all([
            executeQuery(
              whiteLabelApi.endpoints.getWhiteLabelCompanies,
              STAT_QUERY_PARAMS
            ),
            executeQuery(whiteLabelApi.endpoints.getWhiteLabelCompanies, {
              page: 1,
              limit: 1,
              status: "active",
            }),
            executeQuery(whiteLabelApi.endpoints.getWhiteLabelCompanies, {
              page: 1,
              limit: 1,
              status: "inactive",
            }),
          ]);

        const totalPages = Math.max(
          1,
          Number(allCompaniesFirstPage?.pagination?.totalPages || 1)
        );
        const paginatedRequests = [];

        for (let page = 2; page <= totalPages; page += 1) {
          paginatedRequests.push(
            executeQuery(whiteLabelApi.endpoints.getWhiteLabelCompanies, {
              ...STAT_QUERY_PARAMS,
              page,
            })
          );
        }

        const paginatedResults = paginatedRequests.length
          ? await Promise.all(paginatedRequests)
          : [];

        const companies = [
          ...(allCompaniesFirstPage?.items || []),
          ...paginatedResults.flatMap((result) => result?.items || []),
        ];

        const domainCounts = await mapWithConcurrency(
          companies,
          6,
          async (company) => {
            try {
              const domains = await executeQuery(
                whiteLabelApi.endpoints.getWhiteLabelDomainsByCompany,
                company?._id
              );
              return Array.isArray(domains) ? domains.length : 0;
            } catch (domainError) {
              throw new Error(
                getErrorMessage(domainError, "Failed to load domain metrics.")
              );
            }
          }
        );

        const websiteCounts = await mapWithConcurrency(
          companies,
          6,
          async (company) => {
            try {
              const website = await executeQuery(
                whiteLabelApi.endpoints.getWhiteLabelWebsiteByCompany,
                company?._id
              );
              return website ? 1 : 0;
            } catch (websiteError) {
              if (websiteError?.status === 404) {
                return 0;
              }

              throw new Error(
                getErrorMessage(websiteError, "Failed to load website metrics.")
              );
            }
          }
        );

        if (!isMounted) return;

        setStats({
          totalCompanies: Number(
            allCompaniesFirstPage?.pagination?.totalCount || companies.length
          ),
          activeCompanies: Number(
            activeCompaniesResult?.pagination?.totalCount || 0
          ),
          inactiveCompanies: Number(
            inactiveCompaniesResult?.pagination?.totalCount || 0
          ),
          totalDomains: domainCounts.reduce(
            (sum, value) => sum + Number(value || 0),
            0
          ),
          totalWebsites: websiteCounts.reduce(
            (sum, value) => sum + Number(value || 0),
            0
          ),
          recentCompanies: companies.slice(0, 5),
          lastUpdatedAt: new Date().toISOString(),
        });
      } catch (loadError) {
        if (!isMounted) return;
        const message = getErrorMessage(
          loadError,
          "Unable to load white label dashboard."
        );
        setErrorMessage(message);
        toast.error(message);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  const statCards = useMemo(
    () => [
      {
        key: "total-companies",
        label: "Total Companies",
        value: isLoading ? "Loading..." : stats.totalCompanies,
        detail: "Registered white-label tenants",
        tone: "neutral",
        icon: <ApartmentRoundedIcon fontSize="small" />,
      },
      {
        key: "active-companies",
        label: "Active Companies",
        value: isLoading ? "Loading..." : stats.activeCompanies,
        detail: "Companies currently operational",
        tone: "success",
        icon: <CheckCircleRoundedIcon fontSize="small" />,
      },
      {
        key: "inactive-companies",
        label: "Inactive Companies",
        value: isLoading ? "Loading..." : stats.inactiveCompanies,
        detail: "Paused or not yet launched",
        tone: "danger",
        icon: <PauseCircleRoundedIcon fontSize="small" />,
      },
      {
        key: "total-domains",
        label: "Total Domains",
        value: isLoading ? "Loading..." : stats.totalDomains,
        detail: "Domains connected across companies",
        tone: "info",
        icon: <LanguageRoundedIcon fontSize="small" />,
      },
      {
        key: "total-websites",
        label: "Total Websites",
        value: isLoading ? "Loading..." : stats.totalWebsites,
        detail: "Website configurations currently present",
        tone: "violet",
        icon: <PublicRoundedIcon fontSize="small" />,
      },
    ],
    [isLoading, stats]
  );

  const quickActions = useMemo(
    () => [
      {
        key: "create-company",
        title: "Create Company",
        description: "Open Company Management to create and configure a new tenant.",
        icon: <AddBusinessRoundedIcon fontSize="small" />,
        actionLabel: "Open",
        onClick: () => onNavigateSection("companies"),
        disabled: false,
      },
      {
        key: "branding",
        title: "Manage Branding",
        description: "Open company branding using the existing backend records.",
        icon: <PaletteRoundedIcon fontSize="small" />,
        actionLabel: "Open",
        onClick: () => onNavigateSection("branding"),
        disabled: false,
      },
      {
        key: "theme",
        title: "Manage Theme",
        description: "Configure company themes and preview current styling.",
        icon: <FormatPaintRoundedIcon fontSize="small" />,
        actionLabel: "Open",
        onClick: () => onNavigateSection("theme"),
        disabled: false,
      },
      {
        key: "domains",
        title: "Manage Domains",
        description: "Manage connected domains, primary mapping, and verification state.",
        icon: <DomainRoundedIcon fontSize="small" />,
        actionLabel: "Open",
        onClick: () => onNavigateSection("domains"),
        disabled: false,
      },
      {
        key: "website",
        title: "Manage Website",
        description: "Open website configuration, SEO, contact, and publication settings.",
        icon: <LanguageRounded fontSize="small" />,
        actionLabel: "Open",
        onClick: () => onNavigateSection("website"),
        disabled: false,
      },
    ],
    [onNavigateSection]
  );

  const recentActivityItems = useMemo(() => {
    if (!stats.recentCompanies.length) {
      return [];
    }

    return stats.recentCompanies.map((company) => ({
      id: company?._id,
      title: company?.companyName || "Unnamed company",
      subtitle: company?.companyCode || "Company code unavailable",
      timestamp: formatRelativeDate(company?.updatedAt || company?.createdAt),
      status: String(company?.status || "inactive").trim().toLowerCase(),
    }));
  }, [stats.recentCompanies]);

  const emptyState = !isLoading && !errorMessage && stats.totalCompanies === 0;

  return (
    <Stack spacing={3}>
      <FlexibleStatsRow items={statCards} />

      {errorMessage ? (
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          {errorMessage}
        </Alert>
      ) : null}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, xl: 7 }}>
          <WhiteLabelMiniPanel
            title="Recent Activity"
            subtitle="Latest White Label movement inside the existing admin workspace."
            minHeight={336}
          >
            {isLoading ? (
              <DashboardPanelSkeleton rows={4} />
            ) : emptyState ? (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  textAlign: "center",
                  border: `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.common.white, 0.02)
                      : alpha(theme.palette.common.white, 0.74),
                }}
              >
                <Stack spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 54,
                      height: 54,
                      borderRadius: 3,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: theme.palette.primary.main,
                    }}
                  >
                    <StorefrontRoundedIcon />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                    No companies yet
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 520 }}>
                    The dashboard is ready. Once a company is created, activity and connected
                    configuration counts will start appearing here automatically.
                  </Typography>
                  <Button variant="contained" onClick={() => onNavigateSection("companies")}>
                    Open Company Management
                  </Button>
                </Stack>
              </Paper>
            ) : recentActivityItems.length ? (
              <Stack spacing={1.5}>
                {recentActivityItems.map((item) => (
                  <Paper
                    key={item.id}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.common.white, 0.02)
                          : alpha(theme.palette.common.white, 0.82),
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                    >
                      <Stack spacing={0.4}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {item.subtitle}
                        </Typography>
                      </Stack>

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                      >
                        <Chip
                          size="small"
                          label={item.status === "active" ? "Active" : "Inactive"}
                          color={item.status === "active" ? "success" : "default"}
                          variant={item.status === "active" ? "filled" : "outlined"}
                          sx={{ fontWeight: 700 }}
                        />
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {item.timestamp}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.common.white, 0.02)
                      : alpha(theme.palette.common.white, 0.74),
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.75 }}>
                  Activity API is not available yet
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  This panel is using the latest company records for now. A dedicated activity
                  stream can be connected later without changing the dashboard layout.
                </Typography>
              </Paper>
            )}
          </WhiteLabelMiniPanel>
        </Grid>

        <Grid size={{ xs: 12, xl: 5 }}>
          <WhiteLabelMiniPanel
            title="Quick Actions"
            subtitle="Fast entry points into the White Label workflow."
            actions={
              <Chip
                size="small"
                label={
                  isLoading
                    ? "Refreshing metrics"
                    : `Updated ${formatRelativeDate(stats.lastUpdatedAt)}`
                }
                sx={{
                  fontWeight: 700,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.common.white, 0.06)
                      : alpha(theme.palette.primary.main, 0.08),
                }}
              />
            }
            minHeight={336}
          >
            {isLoading ? (
              <DashboardPanelSkeleton rows={5} />
            ) : (
              <Stack spacing={1.5}>
                {quickActions.map((action, index) => (
                  <Paper
                    key={action.key}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.common.white, 0.02)
                          : alpha(theme.palette.common.white, 0.82),
                    }}
                  >
                    <Stack spacing={1.25}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2.5,
                            display: "grid",
                            placeItems: "center",
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            color: theme.palette.primary.main,
                            flexShrink: 0,
                          }}
                        >
                          {action.icon}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                            {action.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {action.description}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Button
                          variant={action.disabled ? "outlined" : "contained"}
                          onClick={action.onClick}
                          disabled={action.disabled}
                          endIcon={!action.disabled ? <LaunchRoundedIcon fontSize="small" /> : null}
                        >
                          {action.actionLabel}
                        </Button>
                        {index === 0 ? (
                          <Chip size="small" label="Available" color="success" sx={{ fontWeight: 700 }} />
                        ) : (
                          <Chip size="small" label="Connected" color="info" variant="outlined" sx={{ fontWeight: 700 }} />
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}

            <Divider />

            <Stack spacing={0.75}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Dashboard Notes
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Company, domain, and website counts are loaded from the frozen backend APIs
                without changing route contracts or admin navigation.
              </Typography>
            </Stack>
          </WhiteLabelMiniPanel>
        </Grid>
      </Grid>
    </Stack>
  );
}
